/**
 * Pixel API tracking utilities for offer impression tracking
 * Manages localStorage-based tracking with cooldown periods to prevent duplicate tracking
 */

import { spotlight_mobile } from "@/generated";
import { light_offer_extended } from "@/lib/models/light_offer_extended";

const PIXEL_TRACKING_KEY = "welcia_pixel_tracking";
const PIXEL_COOLDOWN_HOURS = 1;
const DEBUG = process.env.NODE_ENV === "development";

/**
 * Check if offer has been tracked within the cooldown period for a specific user
 */
export const hasOfferBeenTracked = (
  offerId: number,
  userId?: number,
): boolean => {
  try {
    const trackingData = localStorage.getItem(PIXEL_TRACKING_KEY);
    if (!trackingData) return false;

    const tracking = JSON.parse(trackingData);
    const userOfferKey = `${userId || "guest"}_${offerId}`;
    const lastTracked = tracking[userOfferKey];

    if (!lastTracked) return false;

    const now = Date.now();
    const cooldownMs = PIXEL_COOLDOWN_HOURS * 60 * 60 * 1000;

    return now - lastTracked < cooldownMs;
  } catch (error) {
    if (DEBUG) {
      console.error("Error checking pixel tracking status:", error);
    }
    return false;
  }
};

/**
 * Mark offer as tracked for a specific user
 */
export const markOfferAsTracked = (offerId: number, userId?: number): void => {
  try {
    const trackingData = localStorage.getItem(PIXEL_TRACKING_KEY);
    const tracking = trackingData ? JSON.parse(trackingData) : {};

    const userOfferKey = `${userId || "guest"}_${offerId}`;
    tracking[userOfferKey] = Date.now();

    // Clean up old entries (older than 24 hours)
    const now = Date.now();
    const cleanupMs = 24 * 60 * 60 * 1000;

    Object.keys(tracking).forEach((key) => {
      if (now - tracking[key] > cleanupMs) {
        delete tracking[key];
      }
    });

    localStorage.setItem(PIXEL_TRACKING_KEY, JSON.stringify(tracking));
  } catch (error) {
    if (DEBUG) {
      console.error("Error marking offer as tracked:", error);
    }
  }
};

/**
 * Interface for pixel API tracking data
 */
export interface PixelTrackingData {
  offerId?: number; // Optional offer ID for tracking specific offers
  memberId?: number;
  holderRef?: string;
  retailerId: number;
  storeId?: number;
  offer?: light_offer_extended;
  spotlight?: spotlight_mobile;
}
